import { useEffect, useState, useRef } from 'react'
import { settingsAPI } from '@utils/api'
import { updateBranding } from '@hooks/useLogo'
import AdminLayout from '@components/admin/AdminLayout'
import {
  Save, Loader2, Plus, Trash2, MapPin, Phone,
  Globe, MessageCircle, ImageIcon, Upload, Share2,
  CheckCircle2, AlertCircle, X
} from 'lucide-react'
import toast from 'react-hot-toast'


const fi = 'w-full px-3.5 py-2.5 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-gray-750 transition-all'

const F = ({ label, hint, children, span }) => (
  <div className={span ? 'col-span-2' : ''}>
    <div className="flex items-center justify-between mb-1.5">
      <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</label>
      {hint && <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded-md">{hint}</span>}
    </div>
    {children}
  </div>
)

const Card = ({ id, icon: Icon, title, children }) => (
  <div id={id} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
    <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
      <div className="w-7 h-7 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
        <Icon className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
      </div>
      <h2 className="text-sm font-bold text-gray-800 dark:text-white">{title}</h2>
    </div>
    <div className="p-6">{children}</div>
  </div>
)

const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2.5 cursor-pointer">
    <div onClick={onChange}
      className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${checked ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'}`}>
      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${checked ? 'left-5' : 'left-0.5'}`} />
    </div>
    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
  </label>
)

const BrandingUpload = ({ label, accept, hint, preview, upload, previewClass, previewBox, previewBg, onChange }) => {
  const inputRef  = useRef(null)
  const [drag, setDrag] = useState(false)
  const busy = upload.state === 'uploading'
  const done = upload.state === 'done'
  const err  = upload.state === 'error'

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false)
    const f = e.dataTransfer.files[0]
    if (f) onChange(f)
  }

  return (
    <div>
      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">{label}</p>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDrag(true) }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => !busy && inputRef.current?.click()}
        className={`relative rounded-xl border-2 border-dashed transition-all cursor-pointer overflow-hidden
          ${busy  ? 'border-primary-400 bg-primary-50/50 dark:bg-primary-900/10 cursor-wait' :
            done  ? 'border-green-400 bg-green-50/50 dark:bg-green-900/10' :
            err   ? 'border-red-400 bg-red-50/50 dark:bg-red-900/10' :
            drag  ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/10 scale-[1.01]' :
                    'border-gray-200 dark:border-gray-700 hover:border-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
          }`}
      >
        <input ref={inputRef} type="file" accept={accept} className="hidden"
          onChange={e => { if (e.target.files[0]) onChange(e.target.files[0]); e.target.value = '' }} />

        <div className="flex items-center gap-4 p-4">
          {/* Preview box */}
          <div className={`${previewBox} rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-center overflow-hidden shrink-0 ${previewBg || 'bg-white dark:bg-gray-900'}`}>
            {preview
              ? <img src={preview} alt={label} className={previewClass} />
              : <ImageIcon className="w-5 h-5 text-gray-300" />
            }
          </div>

          {/* Right side */}
          <div className="flex-1 min-w-0">
            {busy ? (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-primary-600 dark:text-primary-400 flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> Uploading…
                  </span>
                  <span className="text-xs font-mono text-gray-500">{upload.pct}%</span>
                </div>
                <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all duration-200"
                    style={{ width: `${upload.pct}%` }}
                  />
                </div>
              </div>
            ) : done ? (
              <p className="text-xs font-semibold text-green-600 dark:text-green-400 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" /> Uploaded successfully
              </p>
            ) : err ? (
              <div>
                <p className="text-xs font-semibold text-red-500 flex items-center gap-1.5 mb-0.5">
                  <AlertCircle className="w-3.5 h-3.5" /> Upload failed
                </p>
                <p className="text-[11px] text-red-400 truncate">{upload.error}</p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                  <Upload className="w-3.5 h-3.5" />
                  {drag ? 'Drop to upload' : 'Click or drag & drop'}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>
              </div>
            )}
          </div>
        </div>

        {/* progress bar overlay at bottom when uploading */}
        {busy && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700">
            <div className="h-full bg-primary-500 transition-all duration-200" style={{ width: `${upload.pct}%` }} />
          </div>
        )}
      </div>
    </div>
  )
}

const emptyOffice = { name: '', address: '', city: '', country: 'India', phone: '', email: '', lat: '', lng: '', isHeadquarters: false }

const defaultSettings = {
  siteName: '', tagline: '', supportEmail: '', supportPhones: [''],
  whatsappNumber: '', workingHours: '',
  social: { facebook: '', twitter: '', linkedin: '', instagram: '', youtube: '', telegram: '', messenger: '' },
  offices: [],
}

const NAV = [
  { id: 'branding',  label: 'Branding',      icon: ImageIcon  },
  { id: 'siteinfo',  label: 'Site Info',      icon: Globe      },
  { id: 'contact',   label: 'Contact',        icon: Phone      },
  { id: 'social',    label: 'Social Media',   icon: Share2     },
  { id: 'offices',   label: 'Offices',        icon: MapPin     },
]

const AdminSettings = () => {
  const [form,             setForm]             = useState(defaultSettings)
  const [loading,          setLoading]          = useState(true)
  const [saving,           setSaving]           = useState(false)
  const [logoPreview,       setLogoPreview]       = useState(null)
  const [adminLogoPreview,  setAdminLogoPreview]  = useState(null)
  const [faviconPreview,    setFaviconPreview]    = useState(null)
  const [logoUpload,       setLogoUpload]       = useState({ pct: 0, state: 'idle', error: null })
  const [adminLogoUpload,  setAdminLogoUpload]  = useState({ pct: 0, state: 'idle', error: null })
  const [faviconUpload,    setFaviconUpload]    = useState({ pct: 0, state: 'idle', error: null })
  const [activeSection,   setActiveSection]   = useState('branding')

  useEffect(() => {
    const load = async () => {
      try {
        const res = await settingsAPI.get()
        const d = res.data
        setLogoPreview(d.logoUrl         ? `${d.logoUrl}?t=${Date.now()}`         : null)
        setAdminLogoPreview(d.adminLogoUrl ? `${d.adminLogoUrl}?t=${Date.now()}`   : null)
        setFaviconPreview(d.faviconUrl     ? `${d.faviconUrl}?t=${Date.now()}`     : null)
        setForm({
          siteName:      d.siteName      || '',
          tagline:       d.tagline       || '',
          supportEmail:  d.supportEmail  || '',
          supportPhones: d.supportPhones?.length ? d.supportPhones : (d.supportPhone ? [d.supportPhone] : ['']),
          whatsappNumber:d.whatsappNumber|| '',
          workingHours:  d.workingHours  || '',
          social: {
            facebook:  d.social?.facebook  || '',
            twitter:   d.social?.twitter   || '',
            linkedin:  d.social?.linkedin  || '',
            instagram: d.social?.instagram || '',
            youtube:   d.social?.youtube   || '',
            telegram:  d.social?.telegram  || '',
            messenger: d.social?.messenger || '',
          },
          offices: d.offices || [],
        })
      } catch { toast.error('Failed to load settings') }
      finally { setLoading(false) }
    }
    load()
  }, [])

  const set = (path, val) => setForm(prev => {
    const next = JSON.parse(JSON.stringify(prev))
    const keys = path.split('.')
    let obj = next
    keys.slice(0, -1).forEach(k => { obj = obj[k] })
    obj[keys[keys.length - 1]] = val
    return next
  })

  const setPhone = (i, val) => setForm(prev => {
    const phones = [...prev.supportPhones]
    phones[i] = val
    return { ...prev, supportPhones: phones }
  })
  const addPhone    = ()  => setForm(prev => ({ ...prev, supportPhones: [...prev.supportPhones, ''] }))
  const removePhone = (i) => setForm(prev => ({ ...prev, supportPhones: prev.supportPhones.filter((_, idx) => idx !== i) }))

  const setOffice    = (i, k, v) => setForm(prev => ({ ...prev, offices: prev.offices.map((o, idx) => idx === i ? { ...o, [k]: v } : o) }))
  const addOffice    = ()        => setForm(prev => ({ ...prev, offices: [...prev.offices, { ...emptyOffice }] }))
  const removeOffice = (i)       => setForm(prev => ({ ...prev, offices: prev.offices.filter((_, idx) => idx !== i) }))

  const handleBrandingUpload = (type, file) => {
    if (!file) return
    const uploadMap  = { logo: setLogoUpload, 'admin-logo': setAdminLogoUpload, favicon: setFaviconUpload }
    const previewMap = { logo: setLogoPreview, 'admin-logo': setAdminLogoPreview, favicon: setFaviconPreview }
    const setUpload  = uploadMap[type]  || setLogoUpload
    const setPreview = previewMap[type] || setLogoPreview
    const labels = { logo: 'Logo', 'admin-logo': 'Admin Logo', favicon: 'Favicon' }
    const label  = labels[type] || 'File'

    setUpload({ pct: 0, state: 'uploading', error: null })

    const fd  = new FormData()
    fd.append('file', file)

    const xhr = new XMLHttpRequest()
    xhr.withCredentials = true

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        setUpload(u => ({ ...u, pct: Math.round((e.loaded / e.total) * 100) }))
      }
    }

    xhr.onload = () => {
      let json
      try { json = JSON.parse(xhr.responseText) } catch { json = {} }
      if (xhr.status >= 200 && xhr.status < 300 && json.url) {
        setUpload({ pct: 100, state: 'done', error: null })
        const urlWithBust = `${json.url}?t=${Date.now()}`
        setPreview(urlWithBust)
        updateBranding({ type, url: urlWithBust })
        toast.success(`${label} updated`)
        setTimeout(() => setUpload({ pct: 0, state: 'idle', error: null }), 2000)
      } else {
        const msg = json.message || `Upload failed (${xhr.status})`
        setUpload({ pct: 0, state: 'error', error: msg })
        toast.error(msg)
      }
    }

    xhr.onerror = () => {
      setUpload({ pct: 0, state: 'error', error: 'Network error — check backend is running' })
      toast.error('Upload failed: network error')
    }

    xhr.open('POST', `/api/admin/settings/upload/${type}`)
    xhr.send(fd)
  }

  const scrollTo = (id) => {
    setActiveSection(id)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const save = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await settingsAPI.update(form)
      toast.success('Settings saved')
    } catch { toast.error('Save failed') }
    finally { setSaving(false) }
  }

  if (loading) return (
    <AdminLayout>
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-7 h-7 animate-spin text-primary-600" />
      </div>
    </AdminLayout>
  )

  return (
    <AdminLayout>
      <form onSubmit={save}>

        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-extrabold text-gray-900 dark:text-white">General Settings</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage branding, contact, social media and office locations</p>
          </div>
          <button type="submit" disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white text-sm font-semibold disabled:opacity-60 shadow-sm transition-all shrink-0">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Settings
          </button>
        </div>

        {/* 2-col layout */}
        <div className="grid lg:grid-cols-5 gap-5 items-start">

          {/* ── Left nav (sticky) ── */}
          <aside className="lg:col-span-1 lg:sticky lg:top-4">
            <nav className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-2 space-y-0.5">
              {NAV.map(n => {
                const Icon = n.icon
                return (
                  <button key={n.id} type="button" onClick={() => scrollTo(n.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left
                      ${activeSection === n.id
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                      }`}>
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{n.label}</span>
                  </button>
                )
              })}
            </nav>
          </aside>

          {/* ── Right content ── */}
          <div className="lg:col-span-4 space-y-5">

            {/* Branding */}
            <Card id="branding" icon={ImageIcon} title="Branding">
              <div className="grid sm:grid-cols-2 gap-6">
                <BrandingUpload
                  label="Site Logo (Light)"
                  accept=".png,.jpg,.jpeg,.svg,.webp"
                  hint="For navbar · PNG, SVG, WEBP · max 2 MB"
                  preview={logoPreview}
                  upload={logoUpload}
                  previewClass="w-full h-full object-contain p-1.5"
                  previewBox="w-24 h-16"
                  onChange={f => handleBrandingUpload('logo', f)}
                />
                <BrandingUpload
                  label="Admin Logo (Dark Sidebar)"
                  accept=".png,.jpg,.jpeg,.svg,.webp"
                  hint="White/light version · PNG, SVG · max 2 MB"
                  preview={adminLogoPreview}
                  upload={adminLogoUpload}
                  previewClass="w-full h-full object-contain p-1.5"
                  previewBox="w-24 h-16"
                  previewBg="bg-[#0f1117]"
                  onChange={f => handleBrandingUpload('admin-logo', f)}
                />
                <BrandingUpload
                  label="Favicon"
                  accept=".png,.jpg,.jpeg,.ico,.svg,.webp"
                  hint="ICO, PNG, SVG · 32×32 px"
                  preview={faviconPreview}
                  upload={faviconUpload}
                  previewClass="w-10 h-10 object-contain"
                  previewBox="w-16 h-16"
                  onChange={f => handleBrandingUpload('favicon', f)}
                />
              </div>
            </Card>

            {/* Site Info */}
            <Card id="siteinfo" icon={Globe} title="Site Info">
              <div className="grid sm:grid-cols-2 gap-4">
                <F label="Site Name">
                  <input value={form.siteName} onChange={e => set('siteName', e.target.value)} className={fi} placeholder="Deoriginlabs" />
                </F>
                <F label="Tagline">
                  <input value={form.tagline} onChange={e => set('tagline', e.target.value)} className={fi} placeholder="DevOps & Cloud Training Institute" />
                </F>
                <F label="Working Hours" span>
                  <input value={form.workingHours} onChange={e => set('workingHours', e.target.value)} className={fi} placeholder="Mon - Sat: 9:00 AM - 7:00 PM" />
                </F>
              </div>
            </Card>

            {/* Contact */}
            <Card id="contact" icon={Phone} title="Contact">
              <div className="grid sm:grid-cols-2 gap-4">
                <F label="Support Email" span>
                  <input type="email" value={form.supportEmail} onChange={e => set('supportEmail', e.target.value)} className={fi} placeholder="contact@deoriginlabs.com" />
                </F>

                {/* Multi-phone */}
                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Support Phone Numbers</label>
                    <button type="button" onClick={addPhone}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-xs font-semibold hover:bg-primary-100 transition-colors">
                      <Plus className="w-3 h-3" /> Add Number
                    </button>
                  </div>
                  <div className="space-y-2">
                    {form.supportPhones.map((ph, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                          <input value={ph} onChange={e => setPhone(i, e.target.value)}
                            className={`${fi} pl-9`} placeholder="+91 98765 43210" />
                        </div>
                        {form.supportPhones.length > 1 && (
                          <button type="button" onClick={() => removePhone(i)}
                            className="p-2 rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <F label="WhatsApp Number" hint="digits only" span>
                  <div className="relative">
                    <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                    <input value={form.whatsappNumber} onChange={e => set('whatsappNumber', e.target.value)} className={`${fi} pl-9`} placeholder="919876543210" />
                  </div>
                </F>
              </div>
            </Card>

            {/* Social Media */}
            <Card id="social" icon={Share2} title="Social Media">
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { key: 'facebook',  label: 'Facebook',   ph: 'https://facebook.com/deoriginlabs' },
                  { key: 'twitter',   label: 'Twitter / X', ph: 'https://twitter.com/deoriginlabs' },
                  { key: 'linkedin',  label: 'LinkedIn',   ph: 'https://linkedin.com/company/deoriginlabs' },
                  { key: 'instagram', label: 'Instagram',  ph: 'https://instagram.com/deoriginlabs' },
                  { key: 'youtube',   label: 'YouTube',    ph: 'https://youtube.com/@deoriginlabs' },
                  { key: 'telegram',  label: 'Telegram',   ph: 'https://t.me/deoriginlabs' },
                  { key: 'messenger', label: 'Messenger',  ph: 'https://m.me/deoriginlabs' },
                ].map(({ key, label, ph }) => (
                  <F key={key} label={label}>
                    <input value={form.social[key]} onChange={e => set(`social.${key}`, e.target.value)} className={fi} placeholder={ph} />
                  </F>
                ))}
              </div>
            </Card>

            {/* Offices */}
            <Card id="offices" icon={MapPin} title="Office Locations">
              <div className="flex justify-end mb-4">
                <button type="button" onClick={addOffice}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 text-sm font-semibold hover:bg-primary-100 transition-colors">
                  <Plus className="w-3.5 h-3.5" /> Add Office
                </button>
              </div>

              {form.offices.length === 0 && (
                <div className="text-center py-10 text-gray-400">
                  <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No offices added yet</p>
                </div>
              )}

              <div className="space-y-4">
                {form.offices.map((office, i) => (
                  <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{office.name || `Office ${i + 1}`}</span>
                        {office.isHeadquarters && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">HQ</span>}
                      </div>
                      <button type="button" onClick={() => removeOffice(i)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="p-4 grid sm:grid-cols-2 gap-x-4 gap-y-3">
                      <F label="Office Name">
                        <input value={office.name} onChange={e => setOffice(i, 'name', e.target.value)} className={fi} placeholder="Bangalore Office" />
                      </F>
                      <F label="City">
                        <input value={office.city} onChange={e => setOffice(i, 'city', e.target.value)} className={fi} placeholder="Bangalore" />
                      </F>
                      <F label="Address" span>
                        <input value={office.address} onChange={e => setOffice(i, 'address', e.target.value)} className={fi} placeholder="HSR Layout, Sector 1" />
                      </F>
                      <F label="Country">
                        <input value={office.country} onChange={e => setOffice(i, 'country', e.target.value)} className={fi} placeholder="India" />
                      </F>
                      <F label="Phone">
                        <input value={office.phone} onChange={e => setOffice(i, 'phone', e.target.value)} className={fi} placeholder="+91 98765 43210" />
                      </F>
                      <F label="Email">
                        <input type="email" value={office.email} onChange={e => setOffice(i, 'email', e.target.value)} className={fi} placeholder="office@deoriginlabs.com" />
                      </F>
                      <F label="Latitude" hint="e.g. 12.9116">
                        <input type="number" step="any" value={office.lat} onChange={e => setOffice(i, 'lat', e.target.value)} className={fi} placeholder="12.9116" />
                      </F>
                      <F label="Longitude" hint="e.g. 77.6389">
                        <input type="number" step="any" value={office.lng} onChange={e => setOffice(i, 'lng', e.target.value)} className={fi} placeholder="77.6389" />
                      </F>
                      <div className="col-span-2 pt-1">
                        <Toggle checked={office.isHeadquarters} onChange={() => setOffice(i, 'isHeadquarters', !office.isHeadquarters)} label="Mark as Headquarters" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

          </div>
        </div>
      </form>
    </AdminLayout>
  )
}

export default AdminSettings
